import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { useParams } from 'react-router';
import { useNavigate } from "react-router";
import { useEffect, useState } from 'react';
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import SickIcon from '@mui/icons-material/Sick';
import HeightIcon from '@mui/icons-material/Height';
import PersonIcon from '@mui/icons-material/Person';
import { configState } from '../../../recoil/atoms';
import HistoryIcon from '@mui/icons-material/History';
import HealingIcon from '@mui/icons-material/Healing';
import WarningIcon from '@mui/icons-material/Warning';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonth  from "@mui/icons-material/CalendarMonth";
import MainCard from '../../../styles/ui-component/cards/MainCard';
import { Add, NavigateBefore, NavigateNext } from '@mui/icons-material';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { BalanceApi, GetStatementResponse } from "../../../api/payment";
import useAuthorization from "../../Authentication/permissions/Authorization";
import { GetPatientResponse, PatientApi } from '../../../api/medical-operations';
import { Card, CardContent, Grid, Typography, LinearProgress, Button, Tooltip, Box, IconButton } from '@mui/material';

const commonCardStyle = {
  borderRadius: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',  
  paddingTop: 1,  
  mt: 1,
  width: "100%",  
  backgroundColor: '#f3f3f3',
  flex: 1,
  flexWrap: "wrap-reverse"
};

const ButtonCardStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',  
  width: '100%',
  borderRadius: '20px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  backgroundColor: (theme: any) => theme.palette.primary.light,
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
  },
};

const StyledButton = styled(Button)(() => ({
  textTransform: 'none',
  padding: '0',
  minWidth: '0',  
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

const ViewPatient = () => {    
  const notesPerPage = 2;
  const navigate = useNavigate();
  let { PatientID } = useParams();
  const config = useRecoilValue(configState);

  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [patient, setPatient] = useState<GetPatientResponse>();
  const [totalBalance, setTotalBalance] = useState<GetStatementResponse>();

  const { canEditTenantPatient, canScheduleAppointment } = useAuthorization();

  const totalPages = Math.ceil((patient?.CriticNotes?.length || 0) / notesPerPage);
  
  const currentNotes = patient?.CriticNotes?.slice(
    (currentPage - 1) * notesPerPage,
    currentPage * notesPerPage
  );
  
  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
  };
  
  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const getPatient = () => {
    const patientApi = new PatientApi(config);
    patientApi
      .getTenantPatientById(PatientID!)
      .then((res) => {
        setIsLoading(false);
        setPatient(res.data!);        
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
        setIsLoading(false);
      });
  };

  const getBalance = () => {
    const Balance = new BalanceApi(config);
    Balance
      .getBalanceStatement(PatientID!)
      .then((res) => {        
        setTotalBalance(res?.data);
      })
      .catch((err) => {        
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
    
  useEffect(() => {
    getPatient();
    getBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MainCard>
        {!isLoading && patient ? (           
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card sx={{ backgroundColor: '#f3f3f3', borderRadius: 2, marginTop: 1 }}>
                    <CardContent>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        mb={2}
                      >
                        <PersonIcon sx={{ fontSize: 60, color: '#5e35b1' }} />
                        <Typography variant="h2" color="#5e35b1" sx={{ mt: 3, textAlign: 'center' }}>
                          {patient.User.FirstName} {patient.User.MiddleName} {patient.User.FamilyName}
                        </Typography>                        
                        {canEditTenantPatient && (
                          <Tooltip title="Edit Patient" sx={{ mt: 2 }}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                navigate(`/patient/${PatientID}/user`);
                              }}
                              sx={{ mt: 1, pr: 4.7, pl: 4.7 }}
                            >
                              Edit Patient
                            </Button>
                          </Tooltip>
                        )}
                        {canScheduleAppointment && (
                          <Tooltip title="Add Appointment">
                            <Button
                              variant="outlined"
                              color="secondary"   
                              sx={{ mt: 1 }}                           
                              onClick={() => {
                                navigate(`/add-appointment/${PatientID}`);
                              }}                  
                            >
                              Add Appointment
                            </Button>
                          </Tooltip>
                        )}   
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card sx={{ backgroundColor: '#f3f3f3', borderRadius: 2, marginTop: 1 }}>
                    <CardContent>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        mb={2}
                      >
                        <Typography variant="h2" color="primary" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
                          Personal Information
                        </Typography>
                        <Box component="div" sx={{ display: 'grid', gap: 2 }}>
                          <Box><strong>Gender:</strong> {patient.User.Gender}</Box>
                          <Box><strong>Date of Birth:</strong> {new Date(patient.User.DateOfBirth).toLocaleDateString()}</Box>
                          <Box><strong>Marital Status:</strong> {patient.User.MaritalStatus}</Box>
                          <Box><strong>Mother's Name:</strong> {patient.User.MotherName}</Box>

                          <Box><strong>Address:</strong> {patient.User.Address?.Street}, {patient.User.Address?.City}, {patient.User.Address?.CountryCode}, {patient.User.Address?.Zip}</Box>
                          <Box><strong>Additional Address Info:</strong> {patient.User.Address?.Additional}</Box>

                          <Box><strong>Phone:</strong> {patient.User.Contact?.PrimaryPhone?.Phone}</Box>
                          <Box><strong>Secondary Phone:</strong> {patient.User.Contact?.SecondaryPhone?.Phone}</Box>
                          <Box><strong>Email:</strong> {patient.User.Contact?.PrimaryEmail?.Email}</Box>

                          <Box><strong>Insurance Company:</strong> {patient.User.Insurance?.InsuranceCompany}</Box>                          
                          <Box><strong>Insurance Expiry Date:</strong> {new Date(patient.User.Insurance?.ExpiryDate!).toLocaleDateString()}</Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>                  

                <Grid item xs={12} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} sx={{ width: '100%', mt: 1 }} gap={2} flexWrap="wrap">                  

                  <StyledButton sx={{ flex: 1 }} onClick={() => navigate(`/patient/${PatientID}/appointments`)}>
                    <Card sx={ButtonCardStyle}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <CalendarMonth color="secondary" sx={{ fontSize: 40, mr: 2 }} />
                        <Typography variant="h4">
                          Show Appointments
                        </Typography>
                      </CardContent>
                    </Card>
                  </StyledButton> 

                  <StyledButton sx={{ flex: 1 }} onClick={() => navigate(`/patient/${PatientID}/treatments`)}>
                    <Card sx={ButtonCardStyle}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <HealingIcon color="secondary" sx={{ fontSize: 40, mr: 2 }} />
                        <Typography variant="h4">
                          Show Treatments
                        </Typography>
                      </CardContent>
                    </Card>
                  </StyledButton> 

                  <StyledButton sx={{ flex: 1 }} onClick={() => navigate(`/patient/${PatientID}/balance`)}>
                    <Card sx={ButtonCardStyle}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: "wrap", justifyContent: "center" }}>
                        <AttachMoneyIcon color="secondary" sx={{ fontSize: 40 }} />
                        <Typography variant="h4">
                          Balance:
                        </Typography>
                        <Typography variant="h2" sx={{marginLeft: 1}}>
                          {totalBalance?.total}
                        </Typography>
                      </CardContent>
                    </Card>
                  </StyledButton> 
                  
                </Grid>
                
                <Grid item display="flex" flexDirection={{ xs: 'column', sm: 'row' }} sx={{ width: '100%', mt: 1 }} gap={2} flexWrap="wrap">
                  <Card sx={commonCardStyle}>        
                    <CardContent>
                      <Box style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" component="div">
                          Age
                        </Typography>
                        <Typography variant="h2">
                          {new Date().getFullYear() - new Date(patient.User.DateOfBirth).getFullYear()} years
                        </Typography>
                      </Box>
                    </CardContent>
                    <Typography>
                      <HistoryIcon color="secondary" sx={{fontSize: 30}}/>
                    </Typography>
                  </Card>    

                  <Card sx={commonCardStyle}>
                    <CardContent>
                      <Box style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" component="div">
                          Height
                        </Typography>
                        <Typography variant="h2">
                          {patient.Height} cm
                        </Typography>
                      </Box>
                    </CardContent>
                    <Typography>
                      <HeightIcon color="secondary" sx={{fontSize: 30}}/>
                    </Typography>
                  </Card>

                  <Card sx={commonCardStyle}>    
                    <CardContent>
                      <Box style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" component="div">
                          Weight
                        </Typography>
                        <Typography variant="h2">
                          {patient.Weight} kg
                        </Typography>
                      </Box>
                    </CardContent>
                    <Typography>
                      <AccessibilityNewIcon color="secondary" sx={{fontSize: 30}}/>
                    </Typography>
                  </Card>

                  <Card sx={commonCardStyle}>      
                    <CardContent>
                      <Box style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" component="div">
                          Blood Type
                        </Typography>
                        <Typography variant="h2">
                          {patient.User.BloodType}
                        </Typography>
                      </Box>
                    </CardContent>
                    <Typography>
                      <BloodtypeIcon color="secondary" sx={{fontSize: 30}}/>
                    </Typography>
                  </Card>                                      
                </Grid>                

                <Grid item sx={{ width: '100%' }}>
                  <Card sx={{ backgroundColor: '#f3f3f3', borderRadius: 2, paddingBottom: 1, paddingTop: 1 }}>
                    <CardContent>                                            
                      <Grid display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h2" color="primary" gutterBottom>
                          Medical Information
                        </Typography>
                        <Tooltip title="Edit Medical Info">
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              navigate(`/patient/${PatientID}/info`, { state: { selectedTab: "1" } });
                            }}
                          >
                            <EditIcon color="secondary" />
                          </Button>
                        </Tooltip>
                      </Grid>

                      <Grid display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                        <Grid display="flex" alignItems="center" gap={2} flex={1}>
                          <WarningIcon color="secondary" sx={{fontSize: 40}}/>
                          <Box>
                            <Typography variant="h3" component="div"> 
                              <strong>Allergies</strong>
                            </Typography>
                            <Typography variant="body2">
                              {patient.Allergies?.join(', ') || 'None'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid display="flex" alignItems="center" gap={2} flex={2}>
                          <SickIcon color="secondary" sx={{fontSize: 40}}/>
                          <Box>
                            <Typography variant="h3" component="div">
                              <strong>Chronic Diseases</strong>
                            </Typography>
                            <Typography variant="body2">
                              {patient.ChronicDiseases?.join(', ') || 'None'}
                            </Typography>
                          </Box>
                        </Grid>
                        
                      </Grid>

                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card sx={{ backgroundColor: '#f3f3f3', borderRadius: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h2" color="primary" gutterBottom>
                          Critical Notes
                        </Typography>
                        <Tooltip title="Add CriticNote">
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              navigate(`/patient/${PatientID}/info`, { state: { selectedTab: "3" } });
                            }}
                          >
                            <Add color="secondary" />
                          </Button>
                        </Tooltip>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                        {currentNotes?.map((note, index) => (
                          <Box key={index} sx={{ flexBasis: 'calc(50% - 16px)' }} mb={2}>
                            <Typography variant="body2">
                              <strong>Note {index + 1 + (currentPage - 1) * notesPerPage}:</strong> {note.Note} <br />
                              <strong>Date Created:</strong> {new Date(note.DateCreated!).toLocaleDateString('en-GB')} <br />
                              <strong>Date Modified:</strong> {new Date(note.DateModified!).toLocaleDateString('en-GB')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                        <IconButton
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                        >
                          <NavigateBefore />
                        </IconButton>
                        <Typography>
                          Page {currentPage} of {totalPages}
                        </Typography>
                        <IconButton
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          <NavigateNext />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

              </Grid>
            </Grid>
          </Grid>
        </>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </MainCard>
    </>    
  );
};
export default ViewPatient;
